import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import classNames from 'classnames';
import { COLUMNS_NAMES } from '../../../common/utils/studentsSortingHelpers';
import { IDEL2, DIBELS8, READING, ESPANOL } from '../../../constants/applications';
import {
    MEASURE_SCORE_DISPLAY_NAMES,
    MEASURE_SCORES_BY_APPLICATION,
} from '../../../constants/assessments';
import { getMeasureScoreBySkillName } from '../../../common/utils/measureScoreBySkillName';
import { useAppConfigContext } from '../../../common/contexts/AppConfigContext';
import TableHeader from '../table-header/TableHeader';
import AssessLink from '../assess-link/AssessLink';
import {
    constructDibels8Link,
    constructIdel2Link,
    PAGE_TYPE,
} from '../../../common/navigation/navigation';

import { getMeasureScores } from '../../../common/utils/studentsListHelpers';
import D8MeasureScore from '../dibels8-summary/D8MeasureScore';
import Idel2MeasureScore from '../idel2-summary/Idel2MeasureScore';
import useAssessIcons from '../../../common/hooks/useAssessIcons';
import getAvailableBenchmarkPeriods from '../../../common/utils/getAvailableBenchmarkPeriods';
import { useAppContext } from '../../../common/contexts/AppContext';
import { BM_PERIODS } from '../../../constants/enrollment';

const getSkillDetailsForPeriod = (
    skillName,
    measureName,
    period,
    { student, classId, results },
    linkConstructor,
    MeasureScoreComponent,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
) => {
    const measureScoreName = getMeasureScoreBySkillName(skillName, measureName);
    const assessmentLink = linkConstructor(student.id, measureName, PAGE_TYPE.REMINDERS, {
        period,
        classId,
    });
    const studentResults = get(results, [period, student.id], []);
    const measureScores = getMeasureScores(studentResults, measureName, measureScoreName);
    const key = `${skillName}${period}`;

    if (!measureScores && isBMAssessmentIconVisible(period)) {
        const assessLinkMode = isBMAssessmentIconEnabled(period)
            ? AssessLink.MODE.OPTIONAL
            : AssessLink.MODE.OPTIONAL_DISABLED;

        return <AssessLink key={key} mode={assessLinkMode} path={assessmentLink} />;
    }

    if (!measureScores) {
        return <div key={key} />;
    }

    return (
        <MeasureScoreComponent
            interpretation={measureScores.interpretation}
            score={measureScores.rawScore}
            studentId={student.id}
            measure={measureName}
            period={period}
            classId={classId}
            resultId={measureScores.resultId}
            key={key}
        />
    );
};

const getSkillRow = (
    skillName,
    measure,
    enrollmentData,
    linkConstructor,
    MeasureScoreComponent,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
    appGroup,
    benchmarkPeriods,
    hasFourthPeriod,
) => (
    <div
        className={classNames('dashboard-measure-info', {
            'dashboard-has-fourth-period': hasFourthPeriod,
        })}
        key={skillName}
    >
        <div className='dashboard-skill'>
            <span className='dashboard-skill-name'>{skillName}</span>
            <span className='dashboard-measure-name'>
                {MEASURE_SCORE_DISPLAY_NAMES[getMeasureScoreBySkillName(skillName, measure)]}
            </span>
        </div>

        {benchmarkPeriods.map(period =>
            getSkillDetailsForPeriod(
                skillName,
                measure,
                period,
                enrollmentData,
                linkConstructor,
                MeasureScoreComponent,
                isBMAssessmentIconEnabled,
                isBMAssessmentIconVisible,
            ),
        )}
    </div>
);

const getDibels8InventoryRows = (
    dibels8AppConfig,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
    enrollmentData,
    benchmarkPeriods,
    hasFourthPeriod,
) => {
    const { student } = enrollmentData;
    const dibels8InventorySkills = get(dibels8AppConfig, 'licensedMeasures', []);

    const d8filteredInventorySkills = MEASURE_SCORES_BY_APPLICATION[DIBELS8].reduce(
        (list, measureScore) => {
            const licensedMeasure = dibels8InventorySkills.find(
                licensedItem =>
                    getMeasureScoreBySkillName(licensedItem.skillName, licensedItem.measure) ===
                        measureScore &&
                    licensedItem.isInventory &&
                    licensedItem.grades.includes(student.grade),
            );
            if (licensedMeasure) {
                return [...list, licensedMeasure];
            }
            return list;
        },
        [],
    );

    if (isEmpty(d8filteredInventorySkills)) {
        return [];
    }

    return d8filteredInventorySkills.map(item =>
        getSkillRow(
            item.skillName,
            item.measure,
            enrollmentData,
            constructDibels8Link,
            D8MeasureScore,
            isBMAssessmentIconEnabled,
            isBMAssessmentIconVisible,
            READING,
            benchmarkPeriods,
            hasFourthPeriod,
        ),
    );
};

const getIdel2InventoryRows = (
    idel2AppConfig,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
    enrollmentData,
    benchmarkPeriods,
    hasFourthPeriod,
) => {
    const { student } = enrollmentData;
    const idel2InventorySkills = get(idel2AppConfig, 'licensedMeasures', []);

    // TODO: filter inventory skills by app once in component
    // instead of for each row
    const filteredInventorySkills = MEASURE_SCORES_BY_APPLICATION[IDEL2].reduce(
        (list, measureScore) => {
            const licensedMeasure = idel2InventorySkills.find(
                licensedItem =>
                    getMeasureScoreBySkillName(licensedItem.skillName, licensedItem.measure) ===
                        measureScore &&
                    licensedItem.isInventory &&
                    licensedItem.grades.includes(student.grade),
            );
            if (licensedMeasure) {
                return [...list, licensedMeasure];
            }
            return list;
        },
        [],
    );

    if (isEmpty(filteredInventorySkills)) {
        return [];
    }
    return filteredInventorySkills.map(item =>
        getSkillRow(
            item.skillName,
            item.measure,
            enrollmentData,
            constructIdel2Link,
            Idel2MeasureScore,
            isBMAssessmentIconEnabled,
            isBMAssessmentIconVisible,
            ESPANOL,
            benchmarkPeriods,
            hasFourthPeriod,
        ),
    );
};

// supported apps with respective get*InventoryRows methods
const inventoryRowsGetterByApp = {
    [DIBELS8]: getDibels8InventoryRows,
    [IDEL2]: getIdel2InventoryRows,
};

const InventorySummary = props => {
    const { student, classId, results, applications } = props;
    const appConfigs = get(useAppConfigContext(), 'appConfigs', {});
    const { fourthPeriodAvailability, selectedAppGroup } = useAppContext();

    // For invertory measures we should render only 3 periods
    // but should leave place in header for 4 periods if needed
    const benchmarkPeriods = useMemo(
        () => getAvailableBenchmarkPeriods(selectedAppGroup, fourthPeriodAvailability),
        [fourthPeriodAvailability, selectedAppGroup],
    );

    const { isBMAssessmentIconEnabled, isBMAssessmentIconVisible } = useAssessIcons(
        benchmarkPeriods,
    );

    const hasFourthPeriod = benchmarkPeriods.length > 3;

    const inventoryRows = Object.keys(inventoryRowsGetterByApp).reduce(
        (rows, app) => [
            ...rows,
            ...(applications.includes(app)
                ? inventoryRowsGetterByApp[app](
                      appConfigs[app],
                      isBMAssessmentIconEnabled,
                      isBMAssessmentIconVisible,
                      {
                          student,
                          classId,
                          results: results[app],
                      },

                      BM_PERIODS,
                      hasFourthPeriod,
                  )
                : []),
        ],
        [],
    );

    return (
        !isEmpty(inventoryRows) && (
            <>
                <TableHeader
                    columns={[COLUMNS_NAMES.INVENTORY, ...BM_PERIODS]}
                    hasFourthPeriod={hasFourthPeriod}
                />
                <div
                    className='dashboard-measure-scores'
                    data-test-id={`${applications.join('-')}-inventory-measure-scores`}
                >
                    {inventoryRows}
                </div>
                <div className='dashboard-empty-divider' />
            </>
        )
    );
};

InventorySummary.propTypes = {
    student: PropTypes.object.isRequired,
    classId: PropTypes.string.isRequired,
    results: PropTypes.object,
    applications: PropTypes.arrayOf(PropTypes.string).isRequired,
};

InventorySummary.defaultProps = {
    results: {},
};

export default InventorySummary;
